import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Header, Footer } from '../components/Layout';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { CHILEAN_REGIONS } from '../constants/chileanLocations';
import { Search, MapPin, Building2, Users, ArrowRight, Phone, FileText, ChevronRight, Brain, HeartHandshake, BadgeCheck, Sparkles, CheckCircle, Star, User, Crown, MessageCircle, DollarSign, Calculator, MessageSquareHeart, BookOpen, GraduationCap, Stethoscope, Activity } from 'lucide-react';
import { InfoBadge } from '../components/TreatmentInfo';
import AnimatedCounter from '../components/AnimatedCounter';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import { CotizacionPromoPopup } from '../components/CotizacionPromoPopup';
import CentersByLocationLinks from '../components/seo/CentersByLocationLinks';
import { API } from '../utils/api';


// Helper function to get the correct image URL
function getImageUrl(url) {
  if (!url) return null;
  if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
    try { url = new URL(url).pathname; } catch(e) { /* keep url as-is */ }
  }
  if (url.startsWith('/api/')) {
    var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
  }
  return url;
}

// Helper function to generate URL-friendly slugs
function generateSlug(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-+|-+$/g, '');
}

const regiones = [
  { name: 'Metropolitana', slug: 'Santiago', count: 45 },
  { name: 'Valparaíso', slug: 'Valparaíso', count: 12 },
  { name: 'Biobío', slug: 'Concepción', count: 8 },
  { name: 'Araucanía', slug: 'Temuco', count: 6 },
  { name: 'Los Lagos', slug: 'Puerto Montt', count: 5 },
  { name: 'Coquimbo', slug: 'La Serena', count: 4 }
];

const cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt'];
// Use CHILEAN_REGIONS from constants for search

const modalidades = [
  { value: 'residencial', label: 'Residencial' },
  { value: 'ambulatorio', label: 'Ambulatorio' },
  { value: 'online', label: 'Online' }
];

const professionalTypes = [
  { value: 'therapist', label: 'Terapeuta en Adicciones' },
  { value: 'psychologist', label: 'Psicólogo' },
  { value: 'psychiatrist', label: 'Psiquiatra' },
  { value: 'counselor', label: 'Consejero' }
];

function LandingPage() {
  var navigate = useNavigate();
  var auth = useAuth();
  var [centers, setCenters] = useState([]);
  var [professionals, setProfessionals] = useState([]);
  var [workshops, setWorkshops] = useState([]);
  var [articles, setArticles] = useState([]);
  var [stats, setStats] = useState({ centers: 0, professionals: 0, workshops: 0, cities: 0, regions: 0 });
  
  var [searchTab, setSearchTab] = useState('centers');
  var [searchCity, setSearchCity] = useState('');
  var [searchRegion, setSearchRegion] = useState('');
  var [searchType, setSearchType] = useState('');
  var [searchProfType, setSearchProfType] = useState('');
  var [searchWorkshopType, setSearchWorkshopType] = useState('');

  useEffect(function() {
    async function load() {
      try {
        var results = await Promise.all([
          axios.get(API + '/centers/public?limit=3'),
          axios.get(API + '/professionals/public?limit=6'),
          axios.get(API + '/workshops/public?limit=6'),
          axios.get(API + '/articles/public?limit=3')
        ]);
        setCenters(results[0].data.centers || []);
        setProfessionals(results[1].data.professionals || []);
        setWorkshops(results[2].data.workshops || []);
        setArticles(results[3].data.articles || []);
      } catch (e) {
        console.error(e);
      }
    }
    load();

    // Load public stats for live counter (non-blocking)
    axios.get(API + '/public/stats').then(function(r) {
      if (r && r.data) setStats(r.data);
    }).catch(function() {});
  }, []);

  function handleSearch(e) {
    e.preventDefault();
    if (searchTab === 'centers') {
      var url = '/centros';
      var params = new URLSearchParams();
      if (searchRegion && searchRegion !== 'all') params.append('region', searchRegion);
      if (searchType && searchType !== 'all') params.append('modality', searchType);
      if (params.toString()) url += '?' + params.toString();
      navigate(url);
    } else if (searchTab === 'professionals') {
      var url = '/profesionales';
      var params = new URLSearchParams();
      if (searchRegion && searchRegion !== 'all') params.append('region', searchRegion);
      if (searchProfType && searchProfType !== 'all') params.append('type', searchProfType);
      if (params.toString()) url += '?' + params.toString();
      navigate(url);
    } else {
      var url = '/talleres';
      var params = new URLSearchParams();
      if (searchRegion && searchRegion !== 'all') params.append('region', searchRegion);
      if (searchWorkshopType && searchWorkshopType !== 'all') params.append('workshop_type', searchWorkshopType);
      if (params.toString()) url += '?' + params.toString();
      navigate(url);
    }
  }

  function getTypeLabel(v) {
    var found = professionalTypes.find(function(t) { return t.value === v; });
    return found ? found.label : v || 'Profesional';
  }

  var workshopTypes = [
    { value: 'yoga', label: 'Yoga' },
    { value: 'meditacion', label: 'Meditación' },
    { value: 'arte', label: 'Arte y Manualidades' },
    { value: 'teatro', label: 'Teatro' },
    { value: 'deportes', label: 'Deportes' },
    { value: 'mindfulness', label: 'Mindfulness' }
  ];

  // Organization Schema for homepage
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SinAdicciones.org",
    "description": "Directorio líder de centros de rehabilitación, profesionales y talleres terapéuticos para adicciones en Chile",
    "url": "https://sinadicciones.org",
    "logo": "https://sinadicciones.org/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "areaServed": "CL",
      "availableLanguage": "Spanish"
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SinAdicciones.org",
    "url": "https://sinadicciones.org",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://sinadicciones.org/centros?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 noise-bg">
      
        <title>Directorio de centros de rehabilitación en Chile | SinAdicciones.org</title>
        <meta name="description" content="Directorio de centros de rehabilitación en Chile: más de 130 centros verificados en las 16 regiones. Residenciales, ambulatorios, comunidades terapéuticas y tratamiento para adicciones a drogas, alcohol, pasta base, marihuana y patología dual." />
        <meta name="keywords" content="directorio centros de rehabilitación Chile, centros rehabilitación Chile, tratamiento adicciones, drogas, alcohol, pasta base, rehabilitación Santiago, psicólogo adicciones, FONASA, talleres, patología dual, comunidad terapéutica" />
        <meta property="og:title" content="Directorio de centros de rehabilitación en Chile" />
        <meta property="og:description" content="Directorio nacional verificado: más de 130 centros en las 16 regiones de Chile. Residenciales, ambulatorios y comunidades terapéuticas." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:secure_url" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:image:alt" content="Directorio de centros de rehabilitación en Chile - SinAdicciones.org" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Directorio de centros de rehabilitación en Chile" />
        <meta name="twitter:description" content="Directorio nacional verificado: más de 130 centros en las 16 regiones de Chile." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href="https://sinadicciones.org/" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        
        {/* FAQ Schema for AI Overviews */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Cuánto cuesta un centro de rehabilitación de drogas en Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los precios varían según modalidad. Tratamiento residencial: $350.000 a $3.000.000 CLP mensuales. Ambulatorio: $350.000 a $600.000 CLP mensuales. Online: desde $200.000 CLP mensuales. También existen opciones gratuitas vía SENDA y FONASA."
              }
            },
            {
              "@type": "Question", 
              "name": "¿Cuánto tiempo dura un tratamiento de rehabilitación de drogas?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "La duración típica es de 6 a 12 meses de tratamiento activo, más 6 a 12 meses adicionales de seguimiento y prevención de recaídas. La fase residencial intensiva puede durar de 1 a 3 meses, seguida de tratamiento ambulatorio."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué tipos de centros de rehabilitación existen en Chile?",
              "acceptedAnswer": {
                "@type": "Answer", 
                "text": "Existen centros residenciales (internación 24/7), ambulatorios (asistencia diurna), semi-residenciales (combinación de ambos) y programas online. La elección depende de la gravedad de la adicción y situación personal del paciente."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cómo elegir el mejor centro de rehabilitación?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Verifica la autorización sanitaria, conoce al equipo profesional, visita las instalaciones, compara precios y metodologías, lee opiniones de ex pacientes y asegúrate de que el enfoque terapéutico sea adecuado para el tipo de adicción a tratar."
              }
            }
          ]
        })}</script>
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1620147512372-9e00421556bb?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Salud mental"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-16">
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight opacity-0 animate-fade-in">
              Encuentra ayuda profesional para adicciones
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed opacity-0 animate-fade-in stagger-1">
              Directorio de centros de rehabilitación, profesionales y talleres terapéuticos en Chile
            </p>
          </div>

          {/* Search Box with 3 Tabs */}
          <div className="max-w-4xl mx-auto opacity-0 animate-fade-in stagger-2">
            <Card className="bg-white/95 backdrop-blur-xl border-0 shadow-2xl overflow-hidden">
              <CardContent className="p-0">
                {/* 3 Tabs */}
                <div className="flex border-b border-slate-200">
                  <button
                    onClick={function() { setSearchTab('centers'); }}
                    className={'flex-1 py-3 md:py-4 px-2 md:px-4 font-medium text-center transition-all duration-200 flex items-center justify-center gap-1.5 md:gap-2 ' + 
                      (searchTab === 'centers' 
                        ? 'bg-emerald-500 text-white' 
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')}
                    data-testid="tab-centers"
                  >
                    <Building2 className="w-4 md:w-5 h-4 md:h-5" />
                    <span className="text-xs md:text-base">Centros</span>
                  </button>
                  <button
                    onClick={function() { setSearchTab('professionals'); }}
                    className={'flex-1 py-3 md:py-4 px-2 md:px-4 font-medium text-center transition-all duration-200 flex items-center justify-center gap-1.5 md:gap-2 ' + 
                      (searchTab === 'professionals' 
                        ? 'bg-emerald-500 text-white' 
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')}
                    data-testid="tab-professionals"
                  >
                    <User className="w-4 md:w-5 h-4 md:h-5" />
                    <span className="text-xs md:text-base">Profesionales</span>
                  </button>
                  <button
                    onClick={function() { setSearchTab('workshops'); }}
                    className={'flex-1 py-3 md:py-4 px-2 md:px-4 font-medium text-center transition-all duration-200 flex items-center justify-center gap-1.5 md:gap-2 ' + 
                      (searchTab === 'workshops' 
                        ? 'bg-emerald-500 text-white' 
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')}
                    data-testid="tab-workshops"
                  >
                    <Users className="w-4 md:w-5 h-4 md:h-5" />
                    <span className="text-xs md:text-base">Talleres</span>
                  </button>
                </div>

                {/* Search Form */}
                <form onSubmit={handleSearch} className="p-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Región</label>
                      <Select value={searchRegion} onValueChange={setSearchRegion}>
                        <SelectTrigger className="h-12 bg-white border-slate-300 text-slate-800 focus:border-emerald-500 focus:ring-emerald-500/20" data-testid="search-region">
                          <MapPin className="w-4 h-4 mr-2 text-emerald-500" />
                          <SelectValue placeholder="Selecciona región" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          <SelectItem value="all" className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">Todas las regiones</SelectItem>
                          {CHILEAN_REGIONS.map(function(r) { 
                            return <SelectItem key={r.value} value={r.value} className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">{r.label}</SelectItem>; 
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {searchTab === 'centers' && (
                      <div className="flex-1">
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Modalidad</label>
                        <Select value={searchType} onValueChange={setSearchType}>
                          <SelectTrigger className="h-12 bg-white border-slate-300 text-slate-800 focus:border-emerald-500 focus:ring-emerald-500/20" data-testid="search-type">
                            <Building2 className="w-4 h-4 mr-2 text-emerald-500" />
                            <SelectValue placeholder="Tipo de tratamiento" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            <SelectItem value="all" className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">Todas las modalidades</SelectItem>
                            {modalidades.map(function(m) { 
                              return <SelectItem key={m.value} value={m.value} className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">{m.label}</SelectItem>; 
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {searchTab === 'professionals' && (
                      <div className="flex-1">
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Especialidad</label>
                        <Select value={searchProfType} onValueChange={setSearchProfType}>
                          <SelectTrigger className="h-12 bg-white border-slate-300 text-slate-800 focus:border-emerald-500 focus:ring-emerald-500/20" data-testid="search-prof-type">
                            <Brain className="w-4 h-4 mr-2 text-emerald-500" />
                            <SelectValue placeholder="Tipo de profesional" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            <SelectItem value="all" className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">Todos los profesionales</SelectItem>
                            {professionalTypes.map(function(t) { 
                              return <SelectItem key={t.value} value={t.value} className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">{t.label}</SelectItem>; 
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {searchTab === 'workshops' && (
                      <div className="flex-1">
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Tipo de Taller</label>
                        <Select value={searchWorkshopType} onValueChange={setSearchWorkshopType}>
                          <SelectTrigger className="h-12 bg-white border-slate-300 text-slate-800 focus:border-emerald-500 focus:ring-emerald-500/20" data-testid="search-workshop-type">
                            <Users className="w-4 h-4 mr-2 text-emerald-500" />
                            <SelectValue placeholder="Tipo de taller" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            <SelectItem value="all" className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">Todos los talleres</SelectItem>
                            {workshopTypes.map(function(t) { 
                              return <SelectItem key={t.value} value={t.value} className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700">{t.label}</SelectItem>; 
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="flex items-end">
                      <Button 
                        type="submit" 
                        size="lg" 
                        className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg w-full md:w-auto shadow-lg hover:shadow-xl transition-all duration-300" 
                        data-testid="search-button"
                      >
                        <Search className="w-5 h-5 mr-2" />
                        Buscar
                      </Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ============ SECCIÓN A — Acceso rápido a Herramientas ============ */}
      <section className="py-14 md:py-20 bg-gradient-to-b from-slate-950 to-slate-900/60" data-testid="home-tools-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-3">Herramientas gratis</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3">Encuentra ayuda en menos de 2 minutos</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Calcula costos, conversa con IA especializada, cotiza con centros reales o haz un test de evaluación. Todo gratis y confidencial.</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {/* Calculadora */}
            <Link to="/calculadora" className="group relative bg-slate-900/80 hover:bg-slate-900 border border-slate-800 hover:border-emerald-500/40 rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1" data-testid="home-tool-calculadora">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <Calculator className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-50 mb-1.5 leading-tight">Calculadora de Costos</h3>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">Estima el precio real de un tratamiento en Chile 2026.</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 group-hover:gap-2 transition-all">Calcular <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>

            {/* Cotizar */}
            <Link to="/cotizar" className="group relative bg-slate-900/80 hover:bg-slate-900 border border-slate-800 hover:border-emerald-500/40 rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1" data-testid="home-tool-cotizar">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <DollarSign className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-50 mb-1.5 leading-tight">Cotizar Tratamiento</h3>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">Recibe propuestas de centros que se ajustan a tu caso.</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 group-hover:gap-2 transition-all">Cotizar gratis <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>

            {/* TerapeutIA */}
            <Link to="/terapeutia" className="group relative bg-gradient-to-br from-teal-500/15 to-slate-900/80 hover:from-teal-500/20 border border-teal-500/30 hover:border-teal-400/60 rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 overflow-hidden" data-testid="home-tool-terapeutia">
              <span className="absolute top-3 right-3 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-teal-400 text-slate-900 uppercase tracking-wider">Nuevo</span>
              <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center mb-4 group-hover:bg-teal-500/25 transition-colors">
                <MessageSquareHeart className="w-6 h-6 text-teal-300" />
              </div>
              <h3 className="text-lg font-bold text-slate-50 mb-1.5 leading-tight">TerapeutIA · Chat IA</h3>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">Habla con una IA especializada solo en adicciones.</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-300 group-hover:gap-2 transition-all">Conversar <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>

            {/* Tests */}
            <Link to="/herramientas" className="group relative bg-slate-900/80 hover:bg-slate-900 border border-slate-800 hover:border-emerald-500/40 rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1" data-testid="home-tool-tests">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <Activity className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-50 mb-1.5 leading-tight">Tests de Adicción</h3>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">AUDIT, DAST, codependencia y más — validados por OMS.</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 group-hover:gap-2 transition-all">Hacer un test <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          </div>

          {/* Banda destacada — Ayuda para Familias */}
          <Link to="/como-ayudar-a-un-familiar-con-adiccion" data-testid="home-familiar-banner" className="group mt-6 md:mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-slate-900/60 to-slate-900/90 p-5 md:p-7 hover:border-emerald-400/60 transition-all duration-300">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-200 text-[11px] font-semibold uppercase tracking-wider mb-2.5">
                Para familias
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-50 leading-tight mb-1.5">
                ¿Tu hijo, pareja o familiar consume drogas o alcohol?
              </h3>
              <p className="text-sm md:text-base text-slate-300 leading-relaxed">
                Test confidencial de 7 preguntas + guía para hablarle sin que se cierre + centros disponibles en tu región.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm whitespace-nowrap transition-colors">
              Cómo ayudarlo <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>

      {/* ============ SECCIÓN B — Educación sobre adicciones ============ */}
      <section className="py-14 md:py-20" data-testid="home-education-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold uppercase tracking-wider mb-3">Información de confianza</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3">Educación sobre adicciones y tratamientos</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Contenido editorial revisado por especialistas. Aprende antes de decidir.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-6">
            {/* Adicciones */}
            <Link to="/adicciones" className="group relative bg-slate-900 hover:bg-slate-900/70 border border-slate-800 hover:border-amber-500/40 rounded-2xl p-7 md:p-8 transition-all duration-300 overflow-hidden" data-testid="home-edu-adicciones">
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-amber-500/5 blur-3xl group-hover:bg-amber-500/10 transition-colors" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center mb-5">
                  <BookOpen className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-50 mb-3">¿Qué son las adicciones?</h3>
                <p className="text-slate-400 mb-5 leading-relaxed">Tipos de adicción (alcohol, cocaína, pasta base, ludopatía, conductuales), señales tempranas, patología dual, prevención y recaída — basado en SENDA, OMS y NIDA.</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 group-hover:gap-2.5 transition-all">Explorar guía completa <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>

            {/* Tratamientos */}
            <Link to="/tratamientos" className="group relative bg-slate-900 hover:bg-slate-900/70 border border-slate-800 hover:border-emerald-500/40 rounded-2xl p-7 md:p-8 transition-all duration-300 overflow-hidden" data-testid="home-edu-tratamientos">
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-emerald-500/5 blur-3xl group-hover:bg-emerald-500/10 transition-colors" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-5">
                  <Stethoscope className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-50 mb-3">Tipos de tratamiento</h3>
                <p className="text-slate-400 mb-5 leading-relaxed">Residencial, ambulatorio, online, comunidad terapéutica, AA/NA, desintoxicación. Compara duración, costo, intensidad y para qué perfil sirve cada uno.</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400 group-hover:gap-2.5 transition-all">Conocer tratamientos <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          </div>

          {/* Sub-links */}
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/blog" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-slate-600 hover:bg-slate-900 text-sm text-slate-300 transition-colors" data-testid="home-edu-blog">
              <FileText className="w-4 h-4 text-emerald-400" />
              Blog · 48+ artículos
            </Link>
            <Link to="/reto-21-dias" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-slate-600 hover:bg-slate-900 text-sm text-slate-300 transition-colors" data-testid="home-edu-reto21">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              Reto 21 Días
            </Link>
            <Link to="/guia-tratamientos" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-slate-600 hover:bg-slate-900 text-sm text-slate-300 transition-colors" data-testid="home-edu-guia">
              <GraduationCap className="w-4 h-4 text-emerald-400" />
              Guía de tratamientos
            </Link>
            <Link to="/estudio-reto-21-dias" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-slate-600 hover:bg-slate-900 text-sm text-slate-300 transition-colors" data-testid="home-edu-estudio">
              <FileText className="w-4 h-4 text-emerald-400" />
              Estudio Reto 21 (PDF)
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials carousel - social proof */}
      <TestimonialsCarousel />

      {/* Featured Centers */}
      {centers.length > 0 && (
        <section className="py-20 md:py-28 bg-slate-900/30" data-testid="featured-centers">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight">Centros Destacados</h2>
                <p className="text-slate-400 mt-2">Centros de rehabilitación verificados</p>
              </div>
              <Button variant="ghost" asChild className="hidden md:flex text-slate-400 hover:text-emerald-400 hover:bg-transparent">
                <Link to="/centros-de-rehabilitacion-drogas-chile">Ver todos <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {centers.map(function(center) {
                var centerSlug = center.slug || center.center_id;
                var rawImage = center.logo || (center.gallery && center.gallery[0]) || (center.images && center.images[0]) || null;
                var centerImage = getImageUrl(rawImage);
                return (
                  <Link key={center.center_id} to={'/centros/' + centerSlug} data-testid={'featured-center-' + center.center_id}>
                    <Card className="bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 overflow-hidden group h-full">
                      <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden relative">
                        {centerImage ? (
                          <img src={centerImage} alt={center.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={function(e) { e.target.style.display = 'none'; }} />
                        ) : (
                          <Building2 className="w-16 h-16 text-slate-300" />
                        )}
                        {/* Badges overlay */}
                        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                          {center.plan_level >= 3 && (
                            <span className="flex items-center gap-1 text-white bg-purple-500 px-2 py-0.5 rounded text-xs font-medium shadow">
                              <Crown className="w-3 h-3" />Elite
                            </span>
                          )}
                          {center.plan_level === 2 && (
                            <span className="flex items-center gap-1 text-white bg-amber-500 px-2 py-0.5 rounded text-xs font-medium shadow">
                              <Crown className="w-3 h-3" />Premium
                            </span>
                          )}
                          {center.plan_level === 1 && (
                            <span className="flex items-center gap-1 text-white bg-blue-500 px-2 py-0.5 rounded text-xs font-medium shadow">
                              <Crown className="w-3 h-3" />Destacado
                            </span>
                          )}
                          {!center.plan_level && center.is_premium && (
                            <span className="flex items-center gap-1 text-white bg-amber-500 px-2 py-0.5 rounded text-xs font-medium shadow">
                              <Crown className="w-3 h-3" />Premium
                            </span>
                          )}
                          {center.is_verified && (
                            <span className="flex items-center gap-1 text-white bg-emerald-500 px-2 py-0.5 rounded text-xs font-medium shadow">
                              <BadgeCheck className="w-3 h-3" />Verificado
                            </span>
                          )}
                        </div>
                        {/* Rating overlay */}
                        {center.average_rating > 0 && (
                          <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded-lg text-sm font-medium">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span>{Number(center.average_rating).toFixed(1)}</span>
                            <span className="text-slate-300 text-xs">({center.total_reviews || 0})</span>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        {/* Location */}
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                          <MapPin className="w-4 h-4 text-emerald-500" />
                          <span className="font-medium">{center.city || 'Chile'}</span>
                        </div>
                        
                        <h3 className="font-semibold text-base text-slate-800 group-hover:text-emerald-600 transition-colors duration-200 line-clamp-2 mb-3">
                          {center.name}
                        </h3>
                        
                        {/* Modality badges with info popup */}
                        {center.modality && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {(Array.isArray(center.modality) ? center.modality : [center.modality]).slice(0, 2).map(function(mod, idx) {
                              return (
                                <InfoBadge key={idx} tipo="modalidad" valor={mod} />
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Specializations */}
                        {center.specializations && center.specializations.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {center.specializations.slice(0, 2).map(function(spec, idx) {
                              var specColors = {
                                'Alcohol': 'bg-amber-100 text-amber-800 border-amber-200',
                                'Drogas': 'bg-red-100 text-red-800 border-red-200'
                              };
                              return (
                                <span key={idx} className={'text-xs px-2 py-0.5 rounded-full font-medium border ' + (specColors[spec] || 'bg-rose-100 text-rose-700 border-rose-200')}>
                                  {spec}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Therapy Approaches - Treatment Type */}
                        {center.therapy_approaches && center.therapy_approaches.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {center.therapy_approaches.slice(0, 2).map(function(approach, idx) {
                              return <InfoBadge key={idx} tipo="enfoque" valor={approach} />;
                            })}
                          </div>
                        )}
                        
                        {/* Services */}
                        {center.services && center.services.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {center.services.slice(0, 2).map(function(s, i) {
                              return <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{s.length > 20 ? s.substring(0, 20) + '...' : s}</span>;
                            })}
                          </div>
                        )}
                        
                        {/* Footer */}
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                          {center.gender && (
                            <span className="flex items-center gap-1 text-xs text-slate-600">
                              <Users className="w-3 h-3" />
                              {center.gender === 'hombres' ? 'Hombres' : center.gender === 'mujeres' ? 'Mujeres' : 'Mixto'}
                            </span>
                          )}
                          {center.whatsapp && (
                            <span className="flex items-center gap-1 text-xs text-green-600 ml-auto">
                              <MessageCircle className="w-3 h-3" />WhatsApp
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Professionals */}
      {professionals.length > 0 && (
        <section className="py-20 md:py-28" data-testid="featured-professionals">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight">Profesionales Destacados</h2>
                <p className="text-slate-400 mt-2">Especialistas en tratamiento de adicciones</p>
              </div>
              <Button variant="ghost" asChild className="hidden md:flex text-slate-400 hover:text-emerald-400 hover:bg-transparent">
                <Link to="/profesionales">Ver todos <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {professionals.map(function(pro) {
                return (
                  <Link key={pro.user_id} to={'/profesionales/' + pro.user_id}>
                    <Card className="bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 group h-full">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4 mb-4">
                          {pro.picture || pro.profile_image ? (
                            <img 
                              src={getImageUrl(pro.profile_image || pro.picture)} 
                              alt={pro.name} 
                              className="w-16 h-16 rounded-xl object-cover ring-2 ring-slate-200 group-hover:ring-emerald-500/50 transition-all duration-300"
                              onError={function(e) { e.target.style.display='none'; }}
                            />
                          ) : (
                            <div className={'w-16 h-16 rounded-xl flex items-center justify-center ring-2 ' + (function() {
                              var n = (pro.name || '').toLowerCase();
                              var fem = ['dra.','maria','maría','maritza','rosa','luz','claudia','andrea','patricia','carolina','valentina','camila','catalina','daniela','fernanda','gabriela','josefa','paula','sofía','sofia','susana','vanessa','laura','carla','marcela','lorena','francisca','natalia','alejandra','carmen','ana','antonia','barbara','elena','florencia','viviana','sandra','roxana','soledad','karina','paola','monica','adriana','karen','priscila'];
                              var isFem = fem.some(function(f) { return n.includes(f); });
                              return isFem ? 'bg-purple-100 ring-purple-200' : 'bg-teal-100 ring-teal-200';
                            })()}>
                              <span className={'font-bold text-base ' + (function() {
                                var n = (pro.name || '').toLowerCase();
                                var fem = ['dra.','maria','maría','maritza','rosa','luz','claudia','andrea','patricia','carolina','daniela','gabriela','paula','sofía','sofia','laura','carla','natalia','alejandra','carmen','ana','antonia','elena','sandra','monica','adriana','karen'];
                                var isFem = fem.some(function(f) { return n.includes(f); });
                                return isFem ? 'text-purple-500' : 'text-teal-600';
                              })()}>
                                {(pro.name || '').replace(/^(Ps\.|Dr\.|Dra\.|Lic\.|Mg\.)\s*/i, '').split(' ').map(function(w) { return w[0]; }).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold text-slate-800 group-hover:text-emerald-600 transition-colors duration-200">
                              {pro.name}
                            </h3>
                            <p className="text-sm text-emerald-600">{getTypeLabel(pro.professional_type)}</p>
                          </div>
                        </div>
                        {pro.city && (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <MapPin className="w-4 h-4" />{pro.city}
                          </div>
                        )}
                        {pro.specialization && (
                          <p className="text-sm text-slate-600 mt-3 line-clamp-2">{pro.specialization}</p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Workshops */}
      {workshops.length > 0 && (
        <section className="py-20 md:py-28 bg-slate-900/30" data-testid="featured-workshops">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight">Talleres Terapéuticos</h2>
                <p className="text-slate-400 mt-2">Yoga, meditación, arte y más para tu recuperación</p>
              </div>
              <Button variant="ghost" asChild className="hidden md:flex text-slate-400 hover:text-emerald-400 hover:bg-transparent">
                <Link to="/talleres">Ver todos <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workshops.map(function(workshop) {
                var workshopSlug = workshop.slug || workshop.workshop_id;
                return (
                  <Link key={workshop.workshop_id} to={'/talleres/' + workshopSlug} data-testid={'featured-workshop-' + workshop.workshop_id}>
                    <Card className="bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 overflow-hidden group h-full">
                      <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden relative">
                        {workshop.logo || (workshop.gallery && workshop.gallery[0]) ? (
                          <img src={workshop.logo || workshop.gallery[0]} alt={workshop.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <Users className="w-16 h-16 text-slate-300" />
                        )}
                        {workshop.workshop_type && (
                          <span className="absolute top-2 left-2 text-white bg-purple-500 px-2 py-0.5 rounded text-xs font-medium shadow capitalize">
                            {workshop.workshop_type}
                          </span>
                        )}
                        {workshop.average_rating > 0 && (
                          <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded-lg text-sm font-medium">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span>{Number(workshop.average_rating).toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                          <MapPin className="w-4 h-4 text-emerald-500" />
                          <span className="font-medium">{workshop.city || 'Chile'}</span>
                        </div>
                        <h3 className="font-semibold text-base text-slate-800 group-hover:text-emerald-600 transition-colors duration-200 line-clamp-2 mb-2">
                          {workshop.name}
                        </h3>
                        {workshop.modality && (
                          <InfoBadge tipo="modalidad" valor={workshop.modality} />
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
            
            <div className="text-center mt-8 md:hidden">
              <Button variant="outline" asChild className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <Link to="/talleres">Ver todos los talleres <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Banners - Add Profile */}
      <section className="py-16 md:py-24" data-testid="cta-banners">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight text-center mb-12">
            Únete al Directorio
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Add Center */}
            <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 overflow-hidden group hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">¿Tienes un Centro?</h3>
                <p className="text-emerald-100 mb-6 text-sm">Aumenta la visibilidad de tu centro de rehabilitación</p>
                <Button asChild variant="secondary" className="bg-white text-emerald-700 hover:bg-emerald-50 w-full" data-testid="cta-banner-center">
                  <Link to="/agregar-centro">Agregar Centro</Link>
                </Button>
              </CardContent>
            </Card>
            
            {/* Add Professional */}
            <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 overflow-hidden group hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">¿Eres Profesional?</h3>
                <p className="text-blue-100 mb-6 text-sm">Conecta con pacientes que buscan ayuda especializada</p>
                <Button asChild variant="secondary" className="bg-white text-blue-700 hover:bg-blue-50 w-full" data-testid="cta-banner-professional">
                  <Link to="/unirse-profesional">Crear Perfil</Link>
                </Button>
              </CardContent>
            </Card>
            
            {/* Add Workshop */}
            <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0 overflow-hidden group hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">¿Ofreces Talleres?</h3>
                <p className="text-purple-100 mb-6 text-sm">Promociona tus talleres de yoga, arte, mindfulness</p>
                <Button asChild variant="secondary" className="bg-white text-purple-700 hover:bg-purple-50 w-full" data-testid="cta-banner-workshop">
                  <Link to="/registrar-taller">Agregar Taller</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section - Live counter with real data */}
      <section className="py-16 border-y border-slate-800 bg-gradient-to-b from-slate-900/70 to-slate-950" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Directorio nacional verificado</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-50">La red más completa de Chile</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            <div className="text-center p-4 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-emerald-500/40 transition">
              <p className="text-3xl md:text-5xl font-bold text-emerald-400 leading-none">
                <AnimatedCounter value={stats.centers} testId="stat-centers" />
                <span className="text-emerald-500">+</span>
              </p>
              <p className="text-slate-400 text-xs md:text-sm mt-2">Centros listados</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-emerald-500/40 transition">
              <p className="text-3xl md:text-5xl font-bold text-emerald-400 leading-none">
                <AnimatedCounter value={16} testId="stat-regions" />
              </p>
              <p className="text-slate-400 text-xs md:text-sm mt-2">Regiones de Chile</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-emerald-500/40 transition">
              <p className="text-3xl md:text-5xl font-bold text-emerald-400 leading-none">
                <AnimatedCounter value={stats.cities} testId="stat-cities" />
                <span className="text-emerald-500">+</span>
              </p>
              <p className="text-slate-400 text-xs md:text-sm mt-2">Ciudades cubiertas</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-emerald-500/40 transition">
              <p className="text-3xl md:text-5xl font-bold text-emerald-400 leading-none">
                <AnimatedCounter value={stats.workshops} testId="stat-workshops" />
                <span className="text-emerald-500">+</span>
              </p>
              <p className="text-slate-400 text-xs md:text-sm mt-2">Talleres y comunidades</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-emerald-500/40 transition col-span-2 md:col-span-1">
              <p className="text-3xl md:text-5xl font-bold text-emerald-400 leading-none">24/7</p>
              <p className="text-slate-400 text-xs md:text-sm mt-2">Ayuda disponible</p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {articles.length > 0 && (
        <section className="py-20 md:py-28" data-testid="blog-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight">Blog sobre Adicciones</h2>
                <p className="text-slate-400 mt-2">Recursos e información para la recuperación</p>
              </div>
              <Button variant="ghost" asChild className="hidden md:flex text-slate-400 hover:text-emerald-400 hover:bg-transparent">
                <Link to="/blog">Ver todos <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.map(function(article) {
                return (
                  <Link key={article.article_id} to={'/blog/' + article.slug}>
                    <Card className="bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 overflow-hidden group h-full">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                          <FileText className="w-4 h-4" />
                          {article.category || 'General'}
                        </div>
                        <h3 className="font-semibold text-lg text-slate-800 group-hover:text-emerald-600 transition-colors duration-200 line-clamp-2 mb-3">
                          {article.title}
                        </h3>
                        {article.excerpt && (
                          <p className="text-sm text-slate-600 line-clamp-3">{article.excerpt}</p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ SECCIÓN C — Acceso para profesionales y centros ============ */}
      <section className="py-12 md:py-14 bg-slate-900/40 border-y border-slate-800" data-testid="home-pro-center-cta">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/agregar-centro" className="group flex items-center gap-4 p-5 rounded-2xl bg-slate-900 border border-slate-800 hover:border-emerald-500/40 transition-all" data-testid="home-register-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                <Building2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-50 mb-0.5">Soy un centro de rehabilitación</h3>
                <p className="text-xs text-slate-400">Registra tu centro en el directorio más completo de Chile.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all shrink-0" />
            </Link>

            <Link to="/unirse-profesional" className="group flex items-center gap-4 p-5 rounded-2xl bg-slate-900 border border-slate-800 hover:border-emerald-500/40 transition-all" data-testid="home-register-pro">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                <User className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-50 mb-0.5">Soy un profesional de salud mental</h3>
                <p className="text-xs text-slate-400">Crea tu perfil y recibe consultas calificadas.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all shrink-0" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ SECCIÓN D — Centros de Rehabilitación por Ciudad (SEO) ============ */}
      <section className="py-16 md:py-20" data-testid="home-cities-seo-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-3">Directorio nacional</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-4">
              Centros de Rehabilitación de Drogas en Chile
            </h2>
            <p className="text-slate-400 max-w-3xl mx-auto leading-relaxed">
              El directorio más completo del país: <strong className="text-slate-200">+130 centros verificados</strong> en las 16 regiones. Encuentra clínicas residenciales, ambulatorias y comunidades terapéuticas para el tratamiento de adicciones a drogas, alcohol, cocaína, pasta base y más.
            </p>
          </div>

          {/* JSON-LD ItemList */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Centros de Rehabilitación de Drogas en Chile por Ciudad",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Santiago", "url": "https://sinadicciones.org/ciudad/centro-rehabilitacion-santiago" },
              { "@type": "ListItem", "position": 2, "name": "Valparaíso", "url": "https://sinadicciones.org/ciudad/centro-rehabilitacion-valparaiso" },
              { "@type": "ListItem", "position": 3, "name": "Concepción", "url": "https://sinadicciones.org/ciudad/centro-rehabilitacion-concepcion" },
              { "@type": "ListItem", "position": 4, "name": "Viña del Mar", "url": "https://sinadicciones.org/ciudad/centro-rehabilitacion-vina-del-mar" },
              { "@type": "ListItem", "position": 5, "name": "Temuco", "url": "https://sinadicciones.org/ciudad/centro-rehabilitacion-temuco" }
            ]
          }) }} />

          {/* 5 ciudades principales */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {[
              { name: 'Santiago', slug: 'santiago', region: 'Metropolitana', desc: 'Capital de Chile, centro de servicios más grande del país.' },
              { name: 'Valparaíso', slug: 'valparaiso', region: 'Valparaíso', desc: 'Ciudad portuaria con amplia oferta de centros.' },
              { name: 'Viña del Mar', slug: 'vina-del-mar', region: 'Valparaíso', desc: 'Ciudad jardín con centros de alta calidad.' },
              { name: 'Concepción', slug: 'concepcion', region: 'Biobío', desc: 'Principal ciudad del sur de Chile.' },
              { name: 'Temuco', slug: 'temuco', region: 'La Araucanía', desc: 'Capital de La Araucanía y centro regional.' }
            ].map((city) => (
              <Link
                key={city.slug}
                to={`/ciudad/centro-rehabilitacion-${city.slug}`}
                className="group bg-slate-900/70 hover:bg-slate-900 border border-slate-800 hover:border-emerald-500/40 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1"
                data-testid={`home-city-${city.slug}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{city.region}</span>
                </div>
                <h3 className="text-lg font-bold text-slate-50 mb-2 group-hover:text-emerald-300 transition-colors">{city.name}</h3>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">{city.desc}</p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 group-hover:gap-2 transition-all">Ver centros <ArrowRight className="w-3 h-3" /></span>
              </Link>
            ))}
          </div>

          {/* CTA Ver todas las ciudades */}
          <div className="text-center mb-12">
            <Button asChild variant="outline" size="lg" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200" data-testid="home-cities-see-all">
              <Link to="/ciudades">
                Ver todas las ciudades de Chile <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>

          {/* Bloque SEO autoritativo */}
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-slate-50 mb-3 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-emerald-400" />
                ¿Qué encontrarás en este directorio?
              </h3>
              <ul className="space-y-2 text-sm text-slate-400 leading-relaxed">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Directorio completo de <strong className="text-slate-200">centros de rehabilitación de drogas</strong> en todas las ciudades de Chile.</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Información verificada sobre clínicas de <strong className="text-slate-200">tratamiento de adicciones</strong>.</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Precios actualizados 2026 y opiniones de pacientes reales.</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Contacto directo con centros de <strong className="text-slate-200">rehabilitación de alcoholismo</strong>.</li>
              </ul>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-slate-50 mb-3 flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-emerald-400" />
                Tipos de tratamiento disponibles
              </h3>
              <ul className="space-y-2 text-sm text-slate-400 leading-relaxed">
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Tratamiento residencial (internación 24/7)</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Tratamiento ambulatorio y semi-residencial</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Desintoxicación supervisada</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Programas de día y noche</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Terapia online y seguimiento post-alta</li>
              </ul>
            </div>
          </div>

          {/* Centros gratuitos hint */}
          <div className="mt-6 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-center text-sm text-slate-300">
            <strong className="text-emerald-300">¿Buscas opciones gratuitas?</strong> SENDA ofrece tratamiento sin costo llamando al <strong className="text-emerald-400">1412</strong> (línea gratuita 24/7) o explora nuestro directorio de <Link to="/centros-de-rehabilitacion-de-drogas-gratis" className="text-emerald-400 hover:text-emerald-300 underline font-semibold">centros gratuitos</Link>.
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-slate-900/50 border-y border-slate-800" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Card className="bg-gradient-to-r from-emerald-500/10 to-slate-900 border-emerald-500/20 shadow-glow">
            <CardContent className="p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <HeartHandshake className="w-7 h-7 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-50 mb-1">¿Necesitas ayuda inmediata?</h3>
                  <p className="text-slate-400">Contáctanos y te orientaremos en tu proceso de recuperación.</p>
                </div>
              </div>
              <Button 
                size="lg" 
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-8 whitespace-nowrap shadow-glow hover:shadow-glow-lg transition-shadow duration-300" 
                asChild
              >
                <a href="mailto:contacto@sinadicciones.org">
                  <Phone className="w-5 h-5 mr-2" />Contactar
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Preguntas Frecuentes</h2>
          <p className="text-slate-400 text-lg">Todo lo que necesitas saber sobre rehabilitación de adicciones en Chile</p>
        </div>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 md:p-6">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="home-1" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                  ¿Qué es SinAdicciones.org y cómo puede ayudarme?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">
                  SinAdicciones.org es el directorio más completo de centros de rehabilitación, profesionales y talleres terapéuticos de Chile. Te ayudamos a encontrar el tratamiento adecuado comparando opciones verificadas con precios reales, ubicaciones, especialidades y opiniones de otros pacientes. Todo de forma gratuita y confidencial.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="home-2" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                  ¿Cómo sé si yo o un familiar necesita tratamiento profesional?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">
                  Algunas señales de alerta incluyen: incapacidad de controlar el consumo, necesidad de consumir más para sentir el mismo efecto, descuidar responsabilidades, problemas en relaciones personales, y continuar consumiendo a pesar de consecuencias negativas. Si reconoces alguna de estas señales, te recomendamos usar nuestros tests de autoevaluación gratuitos o consultar con un profesional.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="home-3" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                  ¿Cuánto cuesta un tratamiento de rehabilitación en Chile?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">
                  Los costos varían según el tipo de tratamiento. Los programas ambulatorios van desde $200.000 a $500.000 mensuales. Los tratamientos residenciales (internación) pueden costar entre $500.000 y $3.000.000 o más al mes. También existen opciones gratuitas a través de SENDA, FONASA y algunas comunidades terapéuticas. En nuestro directorio puedes filtrar por rango de precios.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="home-4" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                  ¿Qué diferencia hay entre tratamiento ambulatorio y residencial?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">
                  El tratamiento ambulatorio permite mantener tu vida diaria mientras asistes a sesiones programadas (ideal para adicciones leves o moderadas). El tratamiento residencial implica internación en un centro especializado, con atención 24/7, y es recomendado para casos más severos o cuando el entorno familiar no es favorable para la recuperación. Muchos tratamientos combinan ambas modalidades.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="home-5" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                  ¿La información de los centros está verificada?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">
                  Sí, todos los centros con la insignia &quot;Verificado&quot; han pasado por nuestro proceso de validación que incluye verificación de existencia legal, confirmación de servicios ofrecidos, y seguimiento de opiniones de pacientes. Además, los centros &quot;Premium&quot; y &quot;Destacados&quot; han sido revisados con mayor profundidad. Siempre recomendamos visitar el centro antes de tomar una decisión.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="home-6" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                  ¿Puedo encontrar ayuda si no estoy en Santiago?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">
                  Absolutamente. Nuestro directorio incluye centros, profesionales y talleres en todas las regiones de Chile: Santiago, Valparaíso, Concepción, Antofagasta, Temuco, Puerto Montt y más. Además, muchos profesionales ofrecen terapia online, permitiéndote acceder a tratamiento especializado sin importar tu ubicación.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </section>

      {/* SEO interno · enlazado a ciudades y regiones (exact match anchors) */}
      <CentersByLocationLinks variant="general" testId="home-centers-by-location" />

      <Footer />
      <CotizacionPromoPopup />
    </div>
  );
}

export default LandingPage;
